import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useLanguage } from "../i18n/LanguageContext";
import RenewableFileImage from "./RenewableFileImage";
import {
  exportAdminFeedbackCsv,
  getAdminFeedbackAttachment,
  getAdminFeedbacks,
  updateAdminFeedbackStatus,
} from "../services/adminDashboardService";

const STATUS_OPTIONS = ["new", "reviewing", "completed", "archived"];
const CATEGORY_OPTIONS = ["feature", "bug", "general"];

const blobToBase64 = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result || "").split(",")[1] || "");
  reader.onerror = reject;
  reader.readAsDataURL(blob);
});

export default function FeedbackAdminPanel() {
  const { t, lang } = useLanguage();
  const tr = lang !== "en";
  const [filters, setFilters] = useState({ search: "", status: "", category: "", from: "", to: "" });
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [updatingId, setUpdatingId] = useState("");
  const [preview, setPreview] = useState(null);
  const [exporting, setExporting] = useState(false);

  const text = {
    title: t("admin.feedback.title"),
    subtitle: t("admin.feedback.subtitle"),
    search: t("admin.feedback.search"),
    allStatuses: t("admin.feedback.allStatuses"),
    allCategories: t("admin.feedback.allCategories"),
    apply: t("admin.feedback.apply"),
    export: t("admin.feedback.export"),
    empty: t("admin.feedback.empty"),
    attachment: t("admin.feedback.attachment"),
    close: t("admin.feedback.close"),
    previous: t("admin.feedback.previous"),
    next: t("admin.feedback.next"),
  };
  const statusLabel = {
    new: t("admin.feedback.status.new"),
    reviewing: t("admin.feedback.status.reviewing"),
    completed: t("admin.feedback.status.completed"),
    archived: t("admin.feedback.status.archived"),
  };
  const categoryLabel = {
    feature: t("feedback.category.feature"),
    bug: t("feedback.category.bug"),
    general: t("feedback.category.general"),
  };

  const load = async (page = 1, nextFilters = filters) => {
    try {
      setLoading(true);
      setError("");
      const data = await getAdminFeedbacks({ ...nextFilters, page, limit: 20 });
      setItems(data.feedbacks || []);
      setPagination(data.pagination || { page, pages: 1, total: 0 });
    } catch (loadError) {
      setError(loadError?.message || t("admin.feedback.loadError"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(1); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => { if (preview?.url) URL.revokeObjectURL(preview.url); }, [preview]);
  useEffect(() => {
    if (!preview || typeof document === "undefined") return undefined;
    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event) => { if (event.key === "Escape") setPreview(null); };
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [preview]);

  const applyFilters = (event) => {
    event?.preventDefault?.();
    load(1, filters);
  };

  const changeStatus = async (feedbackId, status) => {
    try {
      setUpdatingId(feedbackId);
      const data = await updateAdminFeedbackStatus(feedbackId, status);
      setItems((current) => current.map((item) => item.id === feedbackId ? { ...item, ...data.feedback } : item));
    } catch (statusError) {
      setError(statusError?.message || t("admin.feedback.statusError"));
    } finally {
      setUpdatingId("");
    }
  };

  const openAttachment = async (attachment) => {
    try {
      setError("");
      const blob = await getAdminFeedbackAttachment(attachment.id);
      const url = URL.createObjectURL(blob);
      setPreview({ url, name: attachment.fileName || text.attachment });
    } catch (attachmentError) {
      setError(attachmentError?.message || t("admin.feedback.attachmentError"));
    }
  };

  const exportCsv = async () => {
    try {
      setExporting(true);
      const blob = await exportAdminFeedbackCsv(filters);
      const fileName = `user-feedback-${new Date().toISOString().slice(0, 10)}.csv`;
      const mimeType = blob.type || "text/csv";
      const nativeBridge = window.webkit?.messageHandlers?.aileNativeBridge;

      if (nativeBridge) {
        const base64 = await blobToBase64(blob);
        nativeBridge.postMessage({ action: "shareFile", base64, mimeType, fileName });
        return;
      }

      if (navigator?.canShare && window.File) {
        const file = new File([blob], fileName, { type: mimeType });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: fileName });
          return;
        }
      }

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (exportError) {
      setError(exportError?.message || t("admin.feedback.csvError"));
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString(tr ? "tr-TR" : "en-US", { dateStyle: "medium", timeStyle: "short" });
  };

  return (
    <section className="adminControlPanel adminFeedbackPanel">
      <div className="adminControlPanelHeader">
        <div className="adminControlPanelTitle">
          <div className="adminControlPanelIcon">💬</div>
          <div>
            <h3>{text.title}</h3>
            <p>{text.subtitle}</p>
          </div>
        </div>
      </div>

      <form className="adminFeedbackFilters" onSubmit={applyFilters}>
        <input value={filters.search} onChange={(event) => setFilters((value) => ({ ...value, search: event.target.value }))} placeholder={text.search} />
        <select value={filters.status} onChange={(event) => setFilters((value) => ({ ...value, status: event.target.value }))}>
          <option value="">{text.allStatuses}</option>
          {STATUS_OPTIONS.map((status) => <option key={status} value={status}>{statusLabel[status]}</option>)}
        </select>
        <select value={filters.category} onChange={(event) => setFilters((value) => ({ ...value, category: event.target.value }))}>
          <option value="">{text.allCategories}</option>
          {CATEGORY_OPTIONS.map((category) => <option key={category} value={category}>{categoryLabel[category]}</option>)}
        </select>
        <input type="date" value={filters.from} onChange={(event) => setFilters((value) => ({ ...value, from: event.target.value }))} aria-label={t("admin.feedback.startDate")} />
        <input type="date" value={filters.to} onChange={(event) => setFilters((value) => ({ ...value, to: event.target.value }))} aria-label={t("admin.feedback.endDate")} />
        <button className="adminPrimaryBtn" type="submit" disabled={loading}>{text.apply}</button>
        <button className="adminGhostBtn" type="button" onClick={exportCsv} disabled={exporting}>{exporting ? "…" : text.export}</button>
      </form>

      <div className="adminFeedbackTotal">{pagination.total || 0} {t("admin.feedback.records")}</div>
      {error && <div className="adminErrorBox">{error}</div>}
      {loading ? (
        <div className="adminFeedbackEmpty">{t("admin.feedback.loading")}</div>
      ) : !items.length ? (
        <div className="adminFeedbackEmpty">{text.empty}</div>
      ) : (
        <div className="adminFeedbackList">
          {items.map((item) => (
            <article className="adminFeedbackItem" key={item.id}>
              <div className="adminFeedbackItemTop">
                <div>
                  <div className="adminFeedbackBadges">
                    <span className={`adminFeedbackCategory ${item.category}`}>{categoryLabel[item.category] || item.category}</span>
                    <span className={`adminFeedbackStatus ${item.status}`}>{statusLabel[item.status] || item.status}</span>
                  </div>
                  <strong>{item.full_name || item.username || item.email || "—"}</strong>
                  <small>{[item.username, item.email, item.family_name].filter(Boolean).join(" · ")}</small>
                </div>
                <time>{formatDate(item.created_at)}</time>
              </div>
              <p className="adminFeedbackMessage">{item.message}</p>
              <div className="adminFeedbackMeta">
                <span>{item.platform || "—"}</span>
                <span>{item.app_version ? `v${item.app_version}` : "—"}</span>
                <span>{item.device_model || "—"}</span>
                <span>{item.os_version || "—"}</span>
                <span>{String(item.language || "tr").toUpperCase()}</span>
              </div>
              {!!item.attachments?.length && (
                <div className="adminFeedbackAttachments">
                  {item.attachments.map((attachment, index) => (
                    <button type="button" key={attachment.id} onClick={() => openAttachment(attachment)}>
                      🖼️ {text.attachment} {index + 1}
                    </button>
                  ))}
                </div>
              )}
              <div className="adminFeedbackActions">
                <label>{t("admin.feedback.status")}</label>
                <select disabled={updatingId === item.id} value={item.status} onChange={(event) => changeStatus(item.id, event.target.value)}>
                  {STATUS_OPTIONS.map((status) => <option key={status} value={status}>{statusLabel[status]}</option>)}
                </select>
                {item.notification_status === "pending" && <span>{t("admin.feedback.pendingNotification")}</span>}
              </div>
            </article>
          ))}
        </div>
      )}

      {pagination.pages > 1 && (
        <div className="adminFeedbackPagination">
          <button type="button" disabled={pagination.page <= 1 || loading} onClick={() => load(pagination.page - 1)}>{text.previous}</button>
          <span>{pagination.page} / {pagination.pages}</span>
          <button type="button" disabled={pagination.page >= pagination.pages || loading} onClick={() => load(pagination.page + 1)}>{text.next}</button>
        </div>
      )}

      {preview && typeof document !== "undefined" && createPortal(
        <div className="adminFeedbackPreviewOverlay" role="presentation" onClick={() => setPreview(null)}>
          <div
            className="adminFeedbackPreviewCard"
            role="dialog"
            aria-modal="true"
            aria-label={`${text.attachment}: ${preview.name}`}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="adminFeedbackPreviewHeader">
              <div><span aria-hidden="true">🖼️</span><strong>{preview.name}</strong></div>
              <button type="button" onClick={() => setPreview(null)} aria-label={text.close}>
                <span aria-hidden="true">×</span><b>{text.close}</b>
              </button>
            </div>
            <div className="adminFeedbackPreviewMedia">
              <RenewableFileImage src={preview.url} alt={preview.name} draggable="false" />
            </div>
          </div>
        </div>,
        document.body
      )}
    </section>
  );
}
